<script setup>
import {
  ref,
  computed,
  useRoute,
  useSanityQuery,
  showError,
  useScrollTopOnMounted,
  useHead,
  useImageSource,
  useDevice,
} from '#imports';
import { getQuery as getArticleQuery } from '~/groq-queries/article';
import { getQuery as getDisclaimerQuery } from '~/groq-queries/disclaimer';
import { getQuery as getNextArticleQuery } from '~/groq-queries/next-article';
import { getPostMetaTags } from '~/utils/get-post-meta-tags';
import { authorSlugs } from '~/constants/author-slugs';
import { hexToRgb } from '~/helpers/hex-to-rgb';

useScrollTopOnMounted();

const route = useRoute();
const { slug } = route.params;
const { previewId } = route.query;
const { isMobile } = useDevice();

const isPreview = computed(() => !!previewId);
const sanityClient = computed(() => (previewId ? 'previewClient' : 'default'));

const articleQuery = getArticleQuery({
  slug,
  isDraft: !!previewId,
  previewId,
});

const disclaimerQuery = getDisclaimerQuery();
const { data: article, refresh } = await useSanityQuery(
  articleQuery,
  {},
  {
    client: sanityClient.value,
  }
);
const { data: disclaimerSitewide } = await useSanityQuery(disclaimerQuery);

const nextArticleQuery = isPreview
  ? null
  : getNextArticleQuery({
      publishedAt: article.value.publishedAt,
    });

let nextArticle;

if (nextArticleQuery) {
  const { data: nextArticleData } = await useSanityQuery(
    nextArticleQuery,
    {},
    {
      client: sanityClient.value,
    }
  );
  nextArticle = nextArticleData;
}

const isDataEmpty = computed(() => !article.value?._id);
if (isDataEmpty.value || article.value.isArticleExternalSourceType)
  showError({ statusCode: 404, message: 'Страница не найдена' });

const pageTitle = computed(
  () => `${article.value.seoTitle || article.value.title} — DOXA`
);
const isArticleCardsType = computed(() => article.value.isArticleCardsType);
const isArticleOpinionType = computed(() => article.value.isArticleOpinionType);
const hasOtherArticles = computed(() => article.value.otherArticles?.length);
const hasCards = computed(() => article.value.cards?.length);

const getColor = (colorFromPicker, defaultColor) =>
  colorFromPicker?.hex || defaultColor;

const shadowColorCustom = computed(() =>
  getColor(article.value.shadowColorFromColorPicker, article.value.shadowColor)
);

const shadowColorCardsCustom = computed(() =>
  getColor(
    article.value.headerShadowColorFromColorPicker,
    article.value.shadowColor
  )
);

const titleMarkColor = computed(() =>
  getColor(
    article.value.titleMarkColorFromColorPicker,
    article.value.titleMarkColor
  )
);

const cardsNavigationItems = computed(() =>
  article.value?.cards?.map(({ content, _key }) => ({
    text: content[0]?.children?.[0]?.text,
    id: _key,
  }))
);
const ogImageUrl = computed(() =>
  useImageSource({ image: article.value.ogImage })
);
const firstAuthor = computed(
  () =>
    article.value.crew.find(({ speciality }) =>
      authorSlugs.includes(speciality.slug.current)
    )?.staff
);
const opinionAuthorId = computed(() =>
  isArticleOpinionType.value ? firstAuthor.value._id : undefined
);

useHead({
  title: pageTitle,
  meta: isDataEmpty.value
    ? []
    : getPostMetaTags({
        isCardsType: isArticleCardsType.value,
        ogImageUrl: ogImageUrl.value,
        ogType: 'article',
        author: firstAuthor.value?.name || '',
        path: route.fullPath,
        ...article.value,
      }),
  script: isDataEmpty.value
    ? []
    : [
        {
          key: 'ld-json-article',
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.value.title,
            description: article.value.subtitle || article.value.description,
            image: ogImageUrl.value,
            datePublished: article.value.publishedAt,
            dateModified: article.value._updatedAt || article.value.publishedAt,
            author: {
              '@type': 'Person',
              name: firstAuthor.value?.name || 'DOXA team',
            },
            publisher: {
              '@type': 'Organization',
              name: 'DOXA',
              url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app',
              logo: {
                '@type': 'ImageObject',
                url: 'https://ei3mgr4e-bsccljbcrq-ez.a.run.app/logo.png',
              },
            },
            url: `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/articles/${article.value.slug.current}`,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://ei3mgr4e-bsccljbcrq-ez.a.run.app/articles/${article.value.slug.current}`,
            },
          }),
        },
      ],
});

const shadowColor = computed(() => {
  const color = shadowColorCustom
    ? hexToRgb(shadowColorCustom.value)
    : '24, 28, 53';

  const colorCards = shadowColorCardsCustom
    ? hexToRgb(shadowColorCardsCustom.value)
    : '24, 28, 53';
  return { '--shadow-color': isArticleCardsType ? colorCards : color };
});

const bottomOffset = ref(0);
const forceHideBottomOffset = ref(0);

const breadcrumbItems = computed(() => {
  const { theme, title } = article.value;
  const breadcrumbs = [
    { text: 'Главная', href: '/' },
    { text: 'Статьи', href: '/articles' },
  ];

  if (theme && theme.name) {
    breadcrumbs.push({
      text: theme.name,
      href: `/themes/${theme.slug.current}`,
    });
  }

  breadcrumbs.push({ text: title });

  return breadcrumbs;
});

if (!process.server) {
  setTimeout(() => {
    const footer = document?.getElementById('footer');
    const moreContent = document?.getElementById('moreContent');

    if (footer && moreContent) {
      bottomOffset.value = footer.offsetHeight + moreContent.offsetHeight;
      forceHideBottomOffset.value = footer.offsetHeight;
    }
  }, 1000);
}
</script>

<template>
  <div
    :class="{
      'article--cards-type': isArticleCardsType,
      'article--no-lead': !article.lead && !isArticleCardsType,
    }"
    :style="shadowColor"
    class="article"
  >
    <LayoutDefaultPage
      v-if="!isDataEmpty"
      :force-transparent-header="isArticleCardsType || isArticleOpinionType"
      :is-shady-header="!isArticleCardsType && !isArticleOpinionType"
      is-animated-header
      is-header-shown
      :breadcrumb-items="breadcrumbItems"
    >
      <template #header-card>
        <div class="article__header-card">
          <ArticleOpinionAuthor
            v-if="isArticleOpinionType"
            :author="firstAuthor"
            :color="titleMarkColor"
            :description="article.opinionAuthorDescription"
          />
          <ArticleHeaderCard
            :article="article"
            :is-article-cards-type="isArticleCardsType"
            :is-article-opinion-type="isArticleOpinionType"
          />
        </div>
      </template>
      <template v-if="isPreview" #progress-bar>
        <PreviewBar @click="refresh" />
      </template>
      <template
        v-if="!isArticleCardsType && bottomOffset > 0"
        #mobile-progress-bar
      >
        <div class="article__progress-bar--mobile">
          <ProgressBarMobile :bottom-offset="bottomOffset" />
        </div>
      </template>
      <div class="article__body">
        <div
          v-if="!isArticleCardsType && bottomOffset > 0"
          class="article__progress-bar"
        >
          <ProgressBar
            :bottom-offset="bottomOffset"
            :force-hide-bottom-offset="forceHideBottomOffset"
          />
        </div>
        <div class="article__content-wrap">
          <DisclaimerBlock
            v-if="disclaimerSitewide.isShowDisclaimer"
            :disclaimer="disclaimerSitewide"
          />
          <GoogleSourceBanner />
          <div
            v-if="article.lead || isArticleCardsType"
            class="article__lead-wrap content-container"
          >
            <div
              v-if="article.lead && !isArticleCardsType"
              class="article__lead"
            >
              <ArticleLead :lead="article.lead" />
            </div>
            <div v-if="isArticleCardsType && hasCards" class="article__cards">
              <ArticleCards :cards="article.cards" />
            </div>
            <div
              v-if="article.lead || isArticleCardsType"
              class="article__crew-wrap"
            >
              <template v-if="isArticleCardsType && hasCards">
                <div class="article__cards-navigation">
                  <ArticleCardsNavigation
                    :navigation-items="cardsNavigationItems"
                  />
                </div>
                <div class="article__cards-navigation-mobile">
                  <ArticleCardsNavigationMobile
                    :navigation-items="cardsNavigationItems"
                  />
                </div>
              </template>
              <ArticleCrew
                :crew="article.crew"
                :general-author-id="opinionAuthorId"
                :is-light="isMobile && isArticleCardsType"
                class="article__crew"
                is-authors
              />
              <ArticleCrew :crew="article.crew" class="article__crew">
                <template #lastElement>
                  <ArticlePublishedDate
                    :date="article.publishedAt"
                    :is-light="isMobile && isArticleCardsType"
                  />
                </template>
              </ArticleCrew>
            </div>
          </div>
          <CrewBlock
            v-if="!article.lead && !isArticleCardsType"
            :article="article"
            :is-article-cards-type="isMobile && isArticleCardsType"
            :opinion-author-id="opinionAuthorId"
          />
          <ArticleContent
            v-if="article.content && !isArticleCardsType"
            :content="article"
            :has-cards="hasCards"
            :without-lead="!article.lead"
          />
        </div>
        <div id="moreContent">
          <div v-if="nextArticle" class="article__next-article">
            <ArticleNextArticle :article="nextArticle" />
          </div>
          <div v-if="hasOtherArticles" class="article__other-articles">
            <ArticleOtherArticles
              v-if="hasOtherArticles"
              :articles="article.otherArticles"
            />
          </div>
        </div>
      </div>
    </LayoutDefaultPage>
  </div>
</template>

<style lang="postcss">
.article {
  --shadow-color: 24, 28, 53;

  &__content-wrap {
    position: relative;
  }

  &__header-card {
    position: relative;
    z-index: -1;
    margin-bottom: calc(var(--main-header-height) * -1);

    @media (--small-vp) {
      height: var(--cover-heigth-mobile);
    }
  }

  &__progress-bar {
    position: fixed;
    z-index: calc(var(--modal-z-index) - 1);
    top: auto;
    right: 50px;
    bottom: 50px;
    left: auto;

    &--mobile {
      position: relative;
      top: 4px;
    }
  }

  &__lead-wrap {
    display: grid;
    padding-top: 40px;
    gap: 40px;
    grid-template-columns: 1fr 240px;
  }

  &__next-article,
  &__other-articles {
    padding-top: 40px;
    border-radius: 24px 24px 0 0;
    background-color: var(--page-background-color);
  }

  &__cards-navigation {
    max-width: 312px;
    margin-bottom: 24px;
  }

  &__cards-navigation-mobile {
    display: none;
  }

  &--cards-type {
    .article__header-card {
      background-color: rgb(var(--shadow-color));
    }

    .member__speciality-name,
    .published-date__caption,
    .published-date {
      color: #fff;
    }

    .article__content-wrap {
      padding-bottom: 148px;
      background: linear-gradient(
        180deg,
        rgb(var(--shadow-color)) 0,
        var(--accent-color) 95%,
        rgba(255, 246, 251, 100%) 100%
      );
    }

    .article__lead-wrap {
      justify-content: center;
      padding-top: 60px;
      gap: 56px;
      grid-template-columns: 1fr 312px;

      @media (--medium-vp) {
        grid-template-columns: 1fr 300px;
        gap: 30px;
      }

      @media (--tablet-vp) {
        grid-template-columns: 1fr 200px;
        gap: 20px;
      }
    }

    .next-article {
      margin-top: -80px;

      .next-article__title {
        color: #fff;
      }
    }
  }

  /* crew block */

  &__crew-wrap {
    display: flex;
    flex-direction: column;

    &--mobile {
      display: none;
    }
  }

  &__crew:not(:last-child) {
    margin-bottom: 16px;
  }

  @media (--desktop-vp) {
    &--no-lead .crew-block {
      position: absolute;
      z-index: 10;
      top: 50px;
      right: 174px;
      display: flex;
      flex-direction: column;
    }
  }

  @media (--small-vp) {
    & .crew-block {
      padding: 20px 12px;
    }

    &__lead-wrap {
      margin-bottom: 30px;
      padding-top: 20px;
      grid-template-columns: 1fr;
      gap: 20px;
    }

    &__lead {
      order: 2;
    }

    &__crew-wrap {
      order: 1;
    }

    &__next-article,
    &__other-articles {
      padding-top: 20px;
      border-radius: 16px 16px 0 0;
    }

    &__cards-navigation {
      display: none;
    }

    &__cards-navigation-mobile {
      position: fixed;
      z-index: calc(var(--modal-z-index) - 1);
      right: 8px;
      bottom: 8px;
      display: block;
    }

    &--cards-type {
      .article__content-wrap {
        margin-bottom: 24px;
        padding-bottom: 36px;
      }

      .article__lead-wrap {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
